import { useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Link, useRouter } from '../../util/router';
import { useAuth } from '../../util/auth';
import wordmark from '../../assets/images/wordmark.png';

function AppThreeBarMenu({ isOpen, onClose }) {
  const auth = useAuth();
  const router = useRouter();
  const user = auth.user;
  const signinPath = `/auth/signin?next=${encodeURIComponent(
    `${router.location.pathname}${router.location.search}`
  )}`;

  const sections = [
    user && {
      title: 'You',
      items: [
        { label: 'My Profile', to: `/profile/${user.username}` },
        { label: 'Want to See', to: `/want-to-see/${user.id}` },
        { label: 'Seen Shows', to: `/seen-shows/${user.id}` },
        { label: 'Following', to: `/following/${user.id}` },
        { label: 'My Lists', to: '/my-lists' },
        { label: 'Account Settings', to: '/settings/general' },
      ],
    },
    {
      title: 'Discover',
      items: [
        { label: 'Featured Lists', to: '/featured' },
      ],
    },
    {
      title: 'Showrunner',
      items: [
        { label: 'About', to: '/about' },
        { label: 'Newsletter', href: 'https://showrunnerapp.substack.com/', external: true },
        { label: 'Instagram', href: 'https://www.instagram.com/showrunnerapp/', external: true },
        { label: 'Get the iOS app', href: '/download-ios' },
        { label: 'Get the Android app', href: '/download-android' },
        { label: 'Terms', to: '/legal/terms-of-service' },
        { label: 'Privacy', to: '/legal/privacy-policy' },
      ],
    },
  ].filter(Boolean);

  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    panelRef.current?.focus();
    const onKeyDown = e => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="relative z-[160]">
      <div className="fixed inset-0 bg-black/40 md:left-20" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="false"
        aria-label="Menu"
        tabIndex={-1}
        className="fixed inset-y-0 right-0 flex w-[85vw] max-w-xs flex-col overflow-y-auto bg-white shadow-xl outline-none md:left-20 md:right-auto"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <img src={wordmark} alt="Showrunner" className="w-40" />
          <button type="button" onClick={onClose} aria-label="Close menu">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <nav className="flex-1 px-5 py-4">
          {sections.map(section => (
            <div key={section.title} className="mb-6">
              <h2 className="mb-2 text-xs uppercase tracking-wider text-slate-400">
                {section.title}
              </h2>
              <ul className="space-y-1">
                {section.items.map(item => (
                  <li key={item.label}>
                    {item.href ? (
                      <a
                        href={item.href}
                        {...(item.external && { target: '_blank', rel: 'noopener noreferrer' })}
                        className="block rounded-md py-1.5 text-lg text-slate-900 hover:text-secondary-red"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        to={item.to}
                        onClick={onClose}
                        className="block rounded-md py-1.5 text-lg text-slate-900 hover:text-secondary-red"
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-slate-200 px-5 py-4">
          {user ? (
            <button
              type="button"
              className="text-lg font-semibold text-secondary-red"
              onClick={() => {
                onClose();
                auth.signout();
              }}
            >
              Sign out &rarr;
            </button>
          ) : (
            <Link to={signinPath} onClick={onClose} className="text-lg font-semibold text-slate-900">
              Sign in &rarr;
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default AppThreeBarMenu;
